import { useEffect, useRef, useState, useMemo, useCallback, useImperativeHandle, forwardRef } from 'react';
import PageCanvas from './PageCanvas';
import PdfAnnotationLayer from './PdfAnnotationLayer';

const RENDER_BUFFER = 1; // render N pages above/below visible range

/**
 * ScrollablePages — Continuous-scroll PDF viewing for normal-sized files.
 *
 * Renders all pages as placeholder divs sized to the page dimensions.
 * Only pages that are visible (+ buffer) get an actual canvas render.
 * An IntersectionObserver tracks which page is most visible and reports
 * it to the parent via onVisiblePageChange for toolbar sync.
 *
 * When `useDomAnnotations` is true, each rendered page also mounts a
 * PDF.js DOM AnnotationLayer (Widget + FreeText rendered as DOM elements,
 * with per-annotation appearance canvases inserted as section children).
 * Page-local state tracks the canvasRenderToken so PdfAnnotationLayer
 * re-renders after each PageCanvas pass.
 *
 * The parent can call scrollToPage(n) via ref for programmatic navigation.
 */
const ScrollablePages = forwardRef(function ScrollablePages(
  { pdfDoc, renderedPdfDoc = pdfDoc, numPages, zoom, pageWidth, pageHeight, onVisiblePageChange, renderOverlay, renderMaskOverlay, renderEditOverlay, renderAnnotationOverlay, hideFormFields, useDomAnnotations, formFields, formValues, onFieldValueChange, onBackgroundClick },
  ref
) {
  const containerRef = useRef(null);
  const pageElsRef = useRef(new Map());
  const ratiosRef = useRef(new Map());
  const callbackRef = useRef(onVisiblePageChange);
  callbackRef.current = onVisiblePageChange;

  const [visiblePages, setVisiblePages] = useState(new Set([1]));
  const [pageSizes, setPageSizes] = useState(new Map());

  // Fetch per-page dimensions for accurate placeholder sizing
  useEffect(() => {
    if (!pdfDoc) return;
    let cancelled = false;
    (async () => {
      const sizes = new Map();
      for (let i = 1; i <= numPages; i++) {
        if (cancelled) return;
        const page = await pdfDoc.getPage(i);
        const vp = page.getViewport({ scale: 1.0 });
        sizes.set(i, { width: vp.width, height: vp.height });
      }
      if (!cancelled) setPageSizes(sizes);
    })();
    return () => { cancelled = true; };
  }, [pdfDoc, numPages]);

  // Expose scrollToPage and container to parent
  useImperativeHandle(ref, () => ({
    scrollToPage(pageNum) {
      const el = pageElsRef.current.get(pageNum);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    get container() {
      return containerRef.current;
    },
  }));

  // IntersectionObserver — tracks which pages are visible and which is "current"
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !pdfDoc) return;

    ratiosRef.current.clear();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const page = parseInt(entry.target.dataset.page, 10);
          if (entry.isIntersecting) {
            ratiosRef.current.set(page, entry.intersectionRatio);
          } else {
            ratiosRef.current.delete(page);
          }
        }

        // Update visible pages (only if changed)
        const nowVisible = new Set(ratiosRef.current.keys());
        setVisiblePages((prev) => {
          if (nowVisible.size === prev.size) {
            let same = true;
            for (const p of nowVisible) {
              if (!prev.has(p)) { same = false; break; }
            }
            if (same) return prev;
          }
          return nowVisible;
        });

        // Report most-visible page to parent
        let maxRatio = 0;
        let maxPage = null;
        for (const [page, ratio] of ratiosRef.current) {
          if (ratio > maxRatio) {
            maxRatio = ratio;
            maxPage = page;
          }
        }
        if (maxPage !== null) {
          callbackRef.current(maxPage);
        }
      },
      {
        root: container,
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      }
    );

    for (const [, el] of pageElsRef.current) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, [pdfDoc, numPages]);

  // Pages to render = visible + buffer
  const pagesToRender = useMemo(() => {
    const set = new Set();
    for (const page of visiblePages) {
      for (let i = page - RENDER_BUFFER; i <= page + RENDER_BUFFER; i++) {
        if (i >= 1 && i <= numPages) set.add(i);
      }
    }
    if (set.size === 0) set.add(1);
    return set;
  }, [visiblePages, numPages]);

  const handleContainerClick = useCallback((e) => {
    if (!e.target.closest('[data-page]')) {
      onBackgroundClick?.();
    }
  }, [onBackgroundClick]);

  return (
    <div ref={containerRef} className="flex-1 overflow-auto bg-gray-100" onClick={handleContainerClick}>
      <div className="flex flex-col items-center justify-center py-4 gap-4 px-8 min-h-full">
        {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => {
          const ps = pageSizes.get(pageNum);
          const phW = (ps ? ps.width : pageWidth) * zoom;
          const phH = (ps ? ps.height : pageHeight) * zoom;
          return (
            <div
              key={pageNum}
              data-page={pageNum}
              ref={(el) => {
                if (el) pageElsRef.current.set(pageNum, el);
                else pageElsRef.current.delete(pageNum);
              }}
              className="shadow-lg shrink-0 relative"
            >
              {pagesToRender.has(pageNum) ? (
                <PageBlock
                  pdfDoc={pdfDoc}
                  renderedPdfDoc={renderedPdfDoc}
                  pageNum={pageNum}
                  zoom={zoom}
                  hideFormFields={hideFormFields}
                  useDomAnnotations={useDomAnnotations}
                  formFields={formFields}
                  formValues={formValues}
                  onFieldValueChange={onFieldValueChange}
                  renderOverlay={renderOverlay}
                  renderMaskOverlay={renderMaskOverlay}
                  renderAnnotationOverlay={renderAnnotationOverlay}
                  renderEditOverlay={renderEditOverlay}
                />
              ) : (
                <div
                  className="bg-white"
                  style={{ width: `${phW}px`, height: `${phH}px` }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

/**
 * PageBlock — Owns the per-page coordination between PageCanvas (writes to
 * annotationCanvasMap) and PdfAnnotationLayer (reads it). Each page gets
 * its own map + render token because the appearance canvases differ per
 * page and the map gets cleared at consumption.
 */
function PageBlock({
  pdfDoc,
  renderedPdfDoc,
  pageNum,
  zoom,
  hideFormFields,
  useDomAnnotations,
  formFields,
  formValues,
  onFieldValueChange,
  renderOverlay,
  renderMaskOverlay,
  renderAnnotationOverlay,
  renderEditOverlay,
}) {
  const annotationCanvasMapRef = useRef(null);
  if (!annotationCanvasMapRef.current) annotationCanvasMapRef.current = new Map();
  const [canvasRenderToken, setCanvasRenderToken] = useState(0);
  const handleRenderComplete = useCallback(() => {
    setCanvasRenderToken((t) => t + 1);
  }, []);

  return (
    <>
      <PageCanvas
        pdfDoc={renderedPdfDoc}
        pageNumber={pageNum}
        scale={zoom}
        className="bg-white"
        hideFormFields={hideFormFields}
        annotationCanvasMap={useDomAnnotations ? annotationCanvasMapRef.current : null}
        onRenderComplete={useDomAnnotations ? handleRenderComplete : null}
      />
      {useDomAnnotations && (
        <PdfAnnotationLayer
          pdfDoc={pdfDoc}
          pageNum={pageNum}
          scale={zoom}
          annotationCanvasMap={annotationCanvasMapRef.current}
          canvasRenderToken={canvasRenderToken}
          formFields={formFields}
          formValues={formValues}
          onFieldValueChange={onFieldValueChange}
        />
      )}
      {renderOverlay && renderOverlay(pageNum)}
      {renderMaskOverlay && renderMaskOverlay(pageNum)}
      {renderAnnotationOverlay && renderAnnotationOverlay(pageNum)}
      {renderEditOverlay && renderEditOverlay(pageNum)}
    </>
  );
}

export default ScrollablePages;
